import { Badge, Flex, Menu, Text } from "@mantine/core";
import { LocationOnOutlined } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import {
    createShortAddressString,
    useTherapproveDrawers
} from "@therapprove/common";
import { FC } from "react";

type LocationItem = {
    locationId: string;
    name: string;
    address1?: string | null;
    city?: string | null;
    stateId?: string | null;
};

type LocationMenuProps = {
    locations: LocationItem[];
};

export const LocationMenu: FC<LocationMenuProps> = ({ locations }) => {
    // Drawers
    const { location: locationDrawer } = useTherapproveDrawers();

    // Data
    if (!locations.length) return null;

    const first = locations[0];
    const remaining = locations.length - 1;

    // Handlers
    const openDrawer = (locationId: string) => {
        locationDrawer.open({ locationId });
    };

    return (
        <Menu
            position="bottom-start"
            withinPortal
            classNames={{ dropdown: "menu-secondary" }}
        >
            {/* Badges */}
            <Menu.Target>
                <Flex
                    align="center"
                    gap={4}
                    style={{ cursor: "pointer" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Badge
                        variant="filled"
                        color="var(--mantine-color-secondary-5)"
                        leftSection={
                            <SvgIcon
                                component={LocationOnOutlined}
                                fontSize="inherit"
                                sx={{ marginBottom: 0.2 }}
                            />
                        }
                    >
                        {first.name}
                    </Badge>
                    {remaining > 0 && (
                        <Badge
                            variant="filled"
                            color="var(--mantine-color-secondary-5)"
                        >
                            +{remaining} more
                        </Badge>
                    )}
                </Flex>
            </Menu.Target>

            {/* Location list */}
            <Menu.Dropdown>
                {locations.map((loc) => {
                    const addressParts = [
                        loc.address1,
                        createShortAddressString({
                            city: loc.city ?? "",
                            stateId: loc.stateId ?? ""
                        })
                    ]
                        .filter(Boolean)
                        .join(", ");

                    return (
                        <Menu.Item
                            key={loc.locationId}
                            onClick={(e) => {
                                e.stopPropagation();
                                openDrawer(loc.locationId);
                            }}
                        >
                            <Flex direction="column">
                                <Text variant="meta-strong">{loc.name}</Text>
                                <Text variant="meta" c="dimmed">
                                    {addressParts}
                                </Text>
                            </Flex>
                        </Menu.Item>
                    );
                })}
            </Menu.Dropdown>
        </Menu>
    );
};
